import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../../api/endpoints'
import { errorMessage } from '../../api/client'
import type { ChannelSummary } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { Icon } from '../../components/Icon'
import { pluralize } from '../../lib/format'
import { useConnection } from '../../state/connection'
import { unnamedConversationLabel } from '../../lib/bots'

/** Admin "All channels" view: channels the admin is not a member of (GET /api/channels?scope=all). */
export function AdminChannelList({ query, activeId }: { query: string; activeId?: string }) {
  const online = useConnection((s) => s.online)
  const [channels, setChannels] = useState<ChannelSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!online) return
    let cancelled = false
    api
      .channels('all')
      .then((all) => {
        if (!cancelled) setChannels(all.filter((c) => !c.isMember))
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e))
      })
    return () => {
      cancelled = true
    }
  }, [online])

  const q = query.trim().toLowerCase()
  const visible = (channels ?? []).filter((c) => !q || (c.name ?? '').toLowerCase().includes(q))

  return (
    <div className="conversation-items">
      <div className="list-section-label">
        <span>Not a member</span>
        <span>{channels ? pluralize(visible.length, 'channel') : ''}</span>
      </div>
      {!online ? (
        <div className="empty-state">
          <Icon name="alert" />
          <strong>You're offline</strong>
          <p>The admin channel directory needs a connection.</p>
        </div>
      ) : error ? (
        <p className="notice error">{error}</p>
      ) : !channels ? (
        <div className="history-status">
          <span className="spinner" /> Loading channels…
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <Icon name="eye" />
          <strong>Nothing to show</strong>
          <p>You're already a member of every matching channel.</p>
        </div>
      ) : (
        visible.map((c) => {
          const name = c.name ?? unnamedConversationLabel(c.type)
          return (
            <Link
              key={c.id}
              to={`/c/${c.id}`}
              state={{ adminSummary: c }}
              className={c.id === activeId ? 'conversation-row active' : 'conversation-row'}
              aria-label={`${name}, view as admin`}
            >
              <Avatar name={name} id={c.id} variant={c.type === 'channel' ? 'channel' : 'person'} />
              <span className="conversation-copy">
                <span className="conversation-title-line">
                  <span className="conversation-name">
                    <span>{name}</span>
                  </span>
                  <span className="admin-row-label">Admin view</span>
                </span>
                <span className="conversation-preview-line">
                  <span className="conversation-preview">
                    {c.type === 'channel' ? pluralize(c.memberCount ?? c.memberIds.length, 'member') : unnamedConversationLabel(c.type)}
                    {c.archivedAt ? ' · Archived' : ''}
                  </span>
                </span>
              </span>
            </Link>
          )
        })
      )}
    </div>
  )
}
